import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, ArrowLeft, Mail, Building2, Clock, Calendar as CalendarIcon } from 'lucide-react';
import { getRegionLabel } from '../lib/regions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PublicProfilePage() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/users/${userId}`, { withCredentials: true })
      .then(res => setProfile(res.data))
      .catch(() => setNotFound(true));
  }, [userId]);

  if (notFound) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-black/70 font-sans">Member not found or account deleted.</p>
              <Link to="/community" className="text-sm underline mt-2 inline-block">← Back to Community Board</Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </main>
      </div>
    );
  }

  const displayName = profile.account_type === 'business' && profile.business_name ? profile.business_name : profile.name;
  const initial = displayName?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <Link to="/community" className="inline-flex items-center gap-1 text-sm font-sans font-semibold text-black/70 hover:text-black" data-testid="back-to-community">
          <ArrowLeft className="w-4 h-4" /> Back to Community Board
        </Link>

        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <Avatar className="w-20 h-20 border-2 border-black">
                <AvatarImage src={profile.picture} />
                <AvatarFallback className="bg-[#ffd703] text-black font-bold text-2xl">{initial}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <CardTitle className="text-2xl font-bold text-black flex items-center gap-2 flex-wrap">
                  <span data-testid="public-profile-name">{displayName}</span>
                  {profile.account_type === 'business' && (
                    <Badge className="bg-[#d8b4fe] text-black">
                      <Building2 className="w-3 h-3 mr-1" /> Business
                    </Badge>
                  )}
                </CardTitle>
                {profile.account_type === 'business' && profile.name && (
                  <p className="text-sm text-black/60 font-sans">Contact: {profile.name}</p>
                )}
                {profile.business_category && (
                  <p className="text-sm text-black/60 font-sans">{profile.business_category}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {profile.email && (
                    <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-1 text-sm bg-[#c7dfe9] border-2 border-black rounded-md px-2 py-1 font-sans font-semibold hover:bg-[#a8d4e6]" data-testid="public-profile-email">
                      <Mail className="w-3 h-3" /> {profile.email}
                    </a>
                  )}
                  {profile.home_region && (
                    <Badge className="bg-[#fef9e7] text-black">{getRegionLabel(profile.home_region)}</Badge>
                  )}
                  <Badge className="bg-[#ffd703] text-black">
                    <Clock className="w-3 h-3 mr-1" /> {(profile.time_credits ?? 0).toFixed(1)} credits
                  </Badge>
                  {profile.created_at && (
                    <Badge variant="outline" className="bg-white">
                      <CalendarIcon className="w-3 h-3 mr-1" /> Since {new Date(profile.created_at).toLocaleDateString()}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {profile.bio && (
              <div>
                <p className="text-xs font-sans font-semibold text-black/60 mb-1">BIO</p>
                <p className="text-black font-sans whitespace-pre-wrap" data-testid="public-profile-bio">{profile.bio}</p>
              </div>
            )}
            {profile.skills?.length > 0 && (
              <div>
                <p className="text-xs font-sans font-semibold text-black/60 mb-1">SKILLS</p>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map(s => (
                    <Badge key={s} className="bg-[#ffd703] text-black">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
            {profile.availability && (
              <div>
                <p className="text-xs font-sans font-semibold text-black/60 mb-1">AVAILABILITY</p>
                <p className="text-black font-sans">{profile.availability}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
